'use client';

import Icon from '@ant-design/icons';
import { Modal, Switch, message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import RightSvg from '@/components/Icon/svgs/right.svg';
import HomeContext from '@/context/HomeContext';
import { useTranslation } from '@/i18n/client';
import {
  EConversationType,
  EGroupRole,
  type ICircleConversation,
  type ICircleSettingDetail,
  type IParticipantUserInfo,
  dissolveCircle,
  removeConversationItem,
  transferCircleOwnership,
  updateCircleSetting
} from '@/service/friend';
import useFriendStore from '@/store/useFriendStore';

import GroupAdminModal from '../GroupAdminModal';
import GroupMemberSelect from '../GroupMemberSelect';
import MemberRequestsPage from './MemberRequestsPage';
import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  circleDetail: ICircleSettingDetail | null;
  circleConversation?: ICircleConversation;
  handleUpdate: () => void;
}

export const CircleManagementModal = (props: IProps): JSX.Element => {
  const { open, setOpen, circleRoute, circleDetail, circleConversation, handleUpdate } = props;
  const { t } = useTranslation();
  const updateFriendCircle = useFriendStore((state) => state.updateFriendCircle);

  // 群管理相关状态
  const [joinVerifyEnabled, setJoinVerifyEnabled] = useState(false);
  const [joinQuestion, setJoinQuestion] = useState('');
  const questionInputRef = useRef<HTMLTextAreaElement>(null);

  // 群管理员页面状态
  const [showAdminPage, setShowAdminPage] = useState(false);

  // 入群申请页面状态
  const [showMemberRequestsPage, setShowMemberRequestsPage] = useState(false);

  // 群转让相关状态
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferMember, setSelectedTransferMember] = useState<IParticipantUserInfo | null>(
    null
  );
  const [transferLoading, setTransferLoading] = useState(false);

  const { setContext, chatCircleRoute } = useContext(HomeContext);

  // 同步管理设置状态
  useEffect(() => {
    if (open && circleDetail) {
      setJoinVerifyEnabled(circleDetail.adminSettings.joinVerifyType === 'ANSWER_QUESTION');
      setJoinQuestion(circleDetail.adminSettings.joinQuestion || '');
    }
  }, [open, circleDetail]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 打开入群申请页面
  const handleOpenMemberRequests = () => {
    setShowMemberRequestsPage(true);
  };

  // 切换验证开关
  const handleToggleJoinVerify = (checked: boolean) => {
    setJoinVerifyEnabled(checked);
    updateCircleSetting({
      route: circleRoute,
      joinVerifyType: checked ? 'ANSWER_QUESTION' : 'NO_VERIFY'
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        handleUpdate();
      })
      .catch((e) => {
        message.error(e.message);
        // 回滚状态
        setJoinVerifyEnabled(!checked);
      });
  };

  // 问题输入框失焦时保存
  const handleQuestionBlur = () => {
    if (joinQuestion !== circleDetail?.adminSettings.joinQuestion) {
      updateCircleSetting({
        route: circleRoute,
        joinQuestion
      })
        .then((res) => {
          if (res.data.code !== 0) {
            throw new Error(res.data.message || t('error.oops'));
          }

          handleUpdate();
        })
        .catch((e) => {
          message.error(e.message);
        });
    }
  };

  // 解散群聊
  const handleDissolve = () => {
    dissolveCircle({ route: circleRoute })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        // 解散成功后删除会话
        return removeConversationItem({
          type: EConversationType.CIRCLE,
          uniqueKey: circleRoute
        });
      })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        message.success(t('circle.dissolveSuccess'));

        // 本地更新删除
        if (circleConversation) {
          updateFriendCircle(circleConversation, true);
        }

        setOpen(false);

        if (circleRoute === chatCircleRoute) {
          setContext({ chatCircleRoute: undefined });
        }
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  // 打开群管理员页面
  const handleOpenAdminPage = () => {
    setShowAdminPage(true);
  };

  // 打开群转让弹窗
  const handleOpenTransfer = () => {
    setShowTransferModal(true);
  };

  // 选择转让成员
  const handleSelectTransferMember = (member: IParticipantUserInfo) => {
    setSelectedTransferMember((prev) => {
      if (prev?.id === member.id) {
        return null;
      }

      return member;
    });
  };

  // 执行群转让
  const handleTransferOwnership = () => {
    if (!selectedTransferMember) {
      message.warning(t('friend.selectMemberTip'));

      return;
    }

    setTransferLoading(true);

    transferCircleOwnership({
      route: circleRoute,
      targetUserId: selectedTransferMember.id
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.transferSuccess'));
        setShowTransferModal(false);
        setSelectedTransferMember(null);
        // 刷新群信息
        handleUpdate();
        // 关闭管理弹窗（因为转让后不再是群主）
        setOpen(false);
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setTransferLoading(false);
      });
  };

  const renderMainContent = () => (
    <div className={styles.managementContainer}>
      {/* 头部 */}
      <div className={styles.managementHeader}>
        <div className={styles.backBtn} onClick={handleClose}>
          <Icon className={styles.backIcon} component={RightSvg} />
        </div>
        <span className={styles.title}>{t('circle.management')}</span>
        <div className={styles.placeholder} />
      </div>

      {/* 进群申请区块 */}
      <div className={styles.managementCard}>
        {/* 收到的进群申请 */}
        <div className={styles.managementRow} onClick={handleOpenMemberRequests}>
          <span className={styles.rowLabel}>{t('circle.memberRequests.title')}</span>
          <div className={styles.rowRight}>
            {(circleDetail?.adminSettings.pendingApplyCount ?? 0) > 0 && (
              <span className={styles.pendingCount}>
                {circleDetail?.adminSettings.pendingApplyCount}
              </span>
            )}
            <Icon className={styles.arrowIcon} component={RightSvg} />
          </div>
        </div>

        {/* 需要群主或管理员验证 */}
        <div className={styles.managementRow}>
          <span className={styles.rowLabel}>{t('circle.requireVerification')}</span>
          <div className={styles.rowRight}>
            <Switch checked={joinVerifyEnabled} onChange={handleToggleJoinVerify} size="small" />
          </div>
        </div>

        {/* 验证问题输入框 - 仅在开关打开时显示 */}
        {joinVerifyEnabled && (
          <textarea
            ref={questionInputRef}
            className={styles.questionTextArea}
            onBlur={handleQuestionBlur}
            onChange={(e) => setJoinQuestion(e.target.value)}
            placeholder={t('circle.questionPlaceholder')}
            value={joinQuestion}
          />
        )}
      </div>

      {/* 群主/管理员区块 - 仅群主可见 */}
      {circleDetail?.role === EGroupRole.OWNER && (
        <div className={styles.managementCard}>
          {/* 群主管理权转让 */}
          <div className={styles.managementRow} onClick={handleOpenTransfer}>
            <span className={styles.rowLabel}>{t('circle.transferOwnership')}</span>
            <div className={styles.rowRight}>
              <Icon className={styles.arrowIcon} component={RightSvg} />
            </div>
          </div>

          {/* 群管理员 */}
          <div className={styles.managementRow} onClick={handleOpenAdminPage}>
            <span className={styles.rowLabel}>{t('circle.admins')}</span>
            <div className={styles.rowRight}>
              <span className={styles.rowValue}>
                {circleDetail.adminSettings.adminCount
                  ? `${circleDetail.adminSettings.adminCount}/${circleDetail.adminSettings.maxAdminCount}`
                  : t('circle.notSet')}
              </span>
              <Icon className={styles.arrowIcon} component={RightSvg} />
            </div>
          </div>
        </div>
      )}

      {/* 解散群按钮 - 仅群主可见 */}
      {circleDetail?.role === EGroupRole.OWNER && (
        <div className={styles.dissolveBtn} onClick={handleDissolve}>
          {t('circle.dissolve')}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        centered
        className={styles.circleManagementModal}
        closable={false}
        footer={null}
        maskClosable
        modalRender={stopPropagationModalRender}
        onCancel={handleClose}
        open={open}
        width={1000}
        zIndex={1100}
      >
        {renderMainContent()}
      </Modal>

      {/* 入群申请弹窗 */}
      <MemberRequestsPage
        circleRoute={circleRoute}
        handleUpdate={handleUpdate}
        open={showMemberRequestsPage}
        setOpen={setShowMemberRequestsPage}
      />

      {/* 群管理员弹窗 */}
      <GroupAdminModal
        circleDetail={circleDetail}
        circleRoute={circleRoute}
        handleUpdate={handleUpdate}
        open={showAdminPage}
        setOpen={setShowAdminPage}
      />

      {/* 群转让弹窗 */}
      <GroupMemberSelect
        circleRoute={circleRoute}
        className="h-[calc(100vh-64px-32px)]"
        confirmText={t('circle.transfer')}
        createLoading={transferLoading}
        handleSelect={handleSelectTransferMember}
        isTransferMode
        loadingText={t('circle.transferring')}
        onCreate={handleTransferOwnership}
        open={showTransferModal}
        selectedIds={selectedTransferMember ? [selectedTransferMember.id] : []}
        setOpen={(isOpen) => {
          setShowTransferModal(isOpen);

          if (!isOpen) {
            setSelectedTransferMember(null);
          }
        }}
        title={t('circle.transferOwnership')}
      />
    </>
  );
};

export default CircleManagementModal;
